import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {AlertService} from '@core/services/alert.service';
import {CampaignsApiService} from '@core/services/api/campaigns-api.service';
import {KanbanBoardComponent, KanbanConfig} from '@shared/components/commons/kanban-board/kanban-board.component';
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {PersonTypeHelper} from '@core/models/person-type.enums';

@Component({
    selector: 'app-campaign-details',
    standalone: true,
    imports: [CommonModule, KanbanBoardComponent, NgbTooltip],
    templateUrl: './campaign-details.component.html',
    styleUrl: './campaign-details.component.scss'
})
export class CampaignDetailsComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private alertService = inject(AlertService);
    private campaignsApiService = inject(CampaignsApiService);

    protected PersonTypeHelper = PersonTypeHelper;
    protected campaignId!: number;
    protected isLoading = signal(true);
    protected campaign = signal<any>(null);
    protected users = signal<any[]>([]);
    protected kanbanConfig!: KanbanConfig;

    protected stats = computed(() => {
        const c = this.campaign();
        if (!c) return null;
        return {
            totalCompanies: c.totalCompanies || 0,
            totalInPipeline: c.totalInPipeline || 0,
            totalValue: c.totalEstimatedValue || 0,
            totalClosed: c.totalClosed || 0
        };
    });

    ngOnInit(): void {
        this.campaignId = +this.route.snapshot.paramMap.get('id')!;
        this.loadCampaign();
        this.loadCampaignUsers();
        this.initializeKanbanConfig();
    }

    private initializeKanbanConfig(): void {
        this.kanbanConfig = {
            loadTasksFn: (params: any) => this.campaignsApiService.getCampaignCompaniesByStatus(this.campaignId, params),
            showAssigneeFilter: true,
            showSearch: true,
            campaignId: this.campaignId,
            users: [],
            allowAssigneeManagement: true,  // Allow editing assignees in campaign view
            allowRemoveFromCampaign: true   // Allow removing tasks from campaign
        };
    }

    private loadCampaign(): void {
        this.isLoading.set(true);

        this.campaignsApiService.findById(this.campaignId).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.campaign.set(response.data);
                } else {
                    this.alertService.error('Erro', 'Falha ao carregar campanha');
                    this.goBack();
                }
                this.isLoading.set(false);
            },
            error: () => {
                this.alertService.operationFailure();
                this.isLoading.set(false);
                this.goBack();
            }
        });
    }

    private loadCampaignUsers(): void {
        this.campaignsApiService.getCampaignUsers().subscribe({
            next: (response: any) => {
                if (response.success && response.data) {
                    this.users.set(response.data);
                    // Update kanban config with users
                    this.kanbanConfig = {
                        ...this.kanbanConfig,
                        users: response.data
                    };
                }
            },
            error: () => {
                console.error('Failed to load campaign users');
            }
        });
    }

    protected formatCurrencyValue(value: number | null | undefined): string {
        if (!value) return 'N/A';
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0
        }).format(value);
    }

    protected formatMonthYear(dateString: string | null | undefined): string {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('pt-BR', {
                month: '2-digit',
                year: 'numeric'
            }).format(date);
        } catch {
            return dateString;
        }
    }


    protected getStatusBadgeClass(status: string): string {
        const classes: Record<string, string> = {
            'ACTIVE': 'bg-success',
            'FINISHED': 'bg-primary'
        };
        return classes[status] || 'bg-secondary';
    }

    protected getStatusLabel(status: string): string {
        const labels: Record<string, string> = {
            'ACTIVE': 'Ativa',
            'FINISHED': 'Finalizada'
        };
        return labels[status] || status;
    }

    protected formatCurrency(value: number): string {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0
        }).format(value);
    }

    protected goBack(): void {
        this.router.navigate(['/campaigns']);
    }
}
